// Coverage composition ("heat") display mapping. The pipeline computes
// topics.composition — {news, analysis, opinion, unenriched, op_left,
// op_right, verdict} — with calibrated thresholds (tasks/coverage_heat.py in
// pano-pipeline-v2). The frontend NEVER re-derives the semantics; this module
// only maps the stored verdict to display copy, with softened non-political
// language for categories where outlet wings don't mean left-vs-right
// (a sports take from a left-leaning outlet is not left commentary).

const SOFT_CATEGORIES = new Set(['entertainment', 'sports', 'misc'])

// Categories where a left/right framing of commentary is meaningful.
function isPolitical(category) {
  return !SOFT_CATEGORIES.has((category || '').toLowerCase())
}

// The commentary's actual shape, stated as counts — "9 left · 2 center ·
// 7 right" — instead of an adjudicated "skews left". A 55/45 split used to
// flatten into a skew verdict, erasing the smaller side (the same
// bucket-rounding sin the spectrum dots exist to avoid); the counts show a
// tilt without deciding it, and a genuine lopside (12 left · 2 right) reads
// louder than any editorial verb. Center = opinion pieces from unwinged
// outlets, derived since the pipeline only stores the wings.
function wingCounts(c) {
  const left = c.op_left || 0
  const right = c.op_right || 0
  const center = Math.max(0, (c.opinion || 0) - left - right)
  const parts = []
  if (left) parts.push(`${left} left`)
  if (center) parts.push(`${center} center`)
  if (right) parts.push(`${right} right`)
  return parts.join(' · ')
}

/**
 * Display model for a topic's composition, or null when there's nothing
 * worth rendering (missing/unknowable composition).
 * Returns { verdict, label, sentence, counts, opinionShare, hot }.
 */
export function compositionDisplay(composition, category) {
  const c =
    typeof composition === 'string' ? safeParse(composition) : composition
  if (!c || !c.verdict) return null

  const total =
    (c.news || 0) + (c.analysis || 0) + (c.opinion || 0) + (c.unenriched || 0)
  if (!total) return null
  const share = Math.round(((c.opinion || 0) / total) * 100)
  const political = isPolitical(category)
  const wings = wingCounts(c)

  const base = {
    verdict: c.verdict,
    counts: {
      news: c.news || 0,
      analysis: c.analysis || 0,
      opinion: c.opinion || 0,
      unenriched: c.unenriched || 0,
    },
    opinionShare: share,
    // The commentary's actual shape ("9 left · 2 center · 7 right") — kept
    // out of the sentence (which only DEFINES the verdict; the glyphs carry
    // the data) but exposed for hover tooltips.
    wings,
    hot: false,
  }

  // Sentences are one parallel family: each defines its verdict in terms of
  // the commentary. Consensus/Contested mirror each other on purpose — "both
  // sides, in agreement" vs "both sides, in disagreement" teaches the whole
  // vocabulary in one read.
  switch (c.verdict) {
    case 'reporting':
      return {
        ...base,
        label: 'Reporting',
        sentence: 'little to no commentary — just the facts.',
      }
    case 'some_commentary':
      return {
        ...base,
        label: 'Some commentary',
        sentence: 'mostly reporting, with a slice of commentary.',
      }
    case 'consensus_opinion':
      return {
        ...base,
        hot: true,
        label: 'Consensus',
        sentence: 'commentary from both sides — in agreement.',
      }
    case 'contested':
      return {
        ...base,
        hot: true,
        label: political ? 'Contested' : 'Widely discussed',
        sentence: political
          ? 'commentary from both sides — in disagreement.'
          : 'much of the coverage is commentary.',
      }
    case 'one_sided_left':
    case 'one_sided_right': {
      const side = c.verdict === 'one_sided_left' ? 'left' : 'right'
      // Precision guard: "only" when the other wing is literally absent,
      // "mostly" otherwise — never "almost entirely" over a 100% wing.
      const minority =
        c.verdict === 'one_sided_left' ? c.op_right || 0 : c.op_left || 0
      return {
        ...base,
        hot: true,
        label: political
          ? `Skews ${side}`
          : 'Widely discussed',
        // "Opinion", not "commentary": the wings are counted over opinion
        // pieces only (that's where sides argue), and a left ANALYSIS piece
        // beside "commentary only from the right" read as a contradiction.
        sentence: political
          ? `opinion ${minority ? 'mostly' : 'only'} from the ${side}.`
          : 'much of the coverage is commentary.',
      }
    }
    default:
      return null
  }
}

function safeParse(value) {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

// Mean member bias — the sort key for the spectrum-row angle layout
// (most-left-argued first). Continuous on purpose: bucketing angles into
// left/right/center columns quantized away exactly the coalitions the
// coverage vocabulary exists to express ("Left & Center", "Center & Right").
export function angleMeanBias(articles) {
  const biases = (articles || []).map((a) => a.outlet?.bias ?? 0)
  if (!biases.length) return 0
  return biases.reduce((sum, b) => sum + b, 0) / biases.length
}
